import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { getAcceptLanguage, setAcceptLanguage } from "../utils/acceptLanguage";

const OPTIONS = [
  { code: "en", short: "EN", labelKey: "English" },
  { code: "ar", short: "ع", labelKey: "العربية" },
];

/**
 * Global language switcher: Accept-Language + UI i18n + RTL.
 *
 * @param {"floating" | "sidebar"} [variant]
 */
const LanguageSwitcher = ({ className = "", variant = "floating" }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [lang, setLang] = useState(getAcceptLanguage);
  const isSidebar = variant === "sidebar";
  const arActive = lang === "ar";

  const select = (code) => {
    if (code === lang) return;
    setAcceptLanguage(code);
    setLang(code);
    i18n.changeLanguage(code);
    queryClient.invalidateQueries();
  };

  const toggle = (
    <div className="relative grid grid-cols-2 rounded-full bg-[#EEF2F4] p-[3px] shrink-0 w-[4.75rem]">
      <span
        className="pointer-events-none absolute top-[3px] bottom-[3px] w-[calc(50%-3px)] rounded-full bg-[linear-gradient(90deg,#27ae60_0%,#2980b9_100%)] shadow-sm transition-[inset-inline-start,left] duration-300 ease-out start-[3px] rtl:start-auto"
        style={{
          insetInlineStart: arActive ? "50%" : "3px",
        }}
        aria-hidden
      />
      {OPTIONS.map((opt) => {
        const active = lang === opt.code;
        return (
          <button
            key={opt.code}
            type="button"
            onClick={() => select(opt.code)}
            title={opt.labelKey}
            aria-pressed={active}
            className={`relative z-[1] h-7 rounded-full text-[11px] font-bold tracking-wide transition-colors duration-200 cursor-pointer ${
              active ? "text-white" : "text-[#5B6B75] hover:text-[#1f2d36]"
            }`}
          >
            {opt.short}
          </button>
        );
      })}
    </div>
  );

  if (isSidebar) {
    return (
      <div
        className={`w-full max-w-full box-border overflow-hidden rounded-2xl bg-white border border-black/5 shadow-sm px-2.5 py-2.5 flex flex-col gap-2.5 ${className}`}
        role="group"
        aria-label={t("language")}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[linear-gradient(135deg,#27ae60_0%,#2980b9_100%)] text-white shrink-0"
            aria-hidden
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M3.5 12h17M12 3.5c2.5 2.8 3.8 5.8 3.8 8.5S14.5 17.7 12 20.5C9.5 17.7 8.2 14.7 8.2 12S9.5 6.3 12 3.5z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="text-[11px] font-semibold text-[#5B6B75] leading-tight min-w-0 truncate">
            {t("language")}
            <br />
            <span className="text-[#1f2d36] font-bold">
              {arActive ? "العربية" : "English"}
            </span>
          </span>
        </div>
        <div className="flex justify-end w-full rtl:justify-start">{toggle}</div>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur-sm border border-black/5 shadow-[0_8px_28px_rgba(15,40,60,0.12)] px-1.5 py-1.5 ${className}`}
      role="group"
      aria-label={t("language")}
    >
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full bg-[linear-gradient(135deg,#27ae60_0%,#2980b9_100%)] text-white shrink-0"
        aria-hidden
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M3.5 12h17M12 3.5c2.5 2.8 3.8 5.8 3.8 8.5S14.5 17.7 12 20.5C9.5 17.7 8.2 14.7 8.2 12S9.5 6.3 12 3.5z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {toggle}
    </div>
  );
};

export default LanguageSwitcher;
